import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Wrench,
  Monitor,
  Mic,
  Radio,
  Timer,
  ClipboardList,
  Printer,
  QrCode,
  Wifi,
  Signal,
  ChevronRight,
  ExternalLink,
  Clock,
  Zap,
  Settings,
  Play,
  Square,
  Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useSeries } from '../../App';

// Tool categories
const toolCategories = [
  {
    id: 'race-day',
    name: 'Race Day Operations',
    description: 'Essential tools for event day management',
    tools: [
      {
        id: 'lineup-monitor',
        name: 'Lineup Monitor',
        description: 'Real-time view of race lineups and staging',
        icon: Monitor,
        path: 'lineup-monitor',
        status: 'available',
        color: 'text-blue-600 bg-blue-100',
      },
      {
        id: 'announcer',
        name: 'Announcer Screen',
        description: 'Driver info and stats for announcers',
        icon: Mic,
        path: 'announcer',
        status: 'available',
        color: 'text-purple-600 bg-purple-100',
      },
      {
        id: 'staging',
        name: 'Staging Display',
        description: 'Pit area staging queue display',
        icon: ClipboardList,
        path: 'staging',
        status: 'coming-soon',
        color: 'text-amber-600 bg-amber-100',
      },
      {
        id: 'flag-stand',
        name: 'Flag Stand Display',
        description: 'Current race status and lap counter',
        icon: Activity,
        path: 'flag-stand',
        status: 'coming-soon',
        color: 'text-emerald-600 bg-emerald-100',
      },
    ]
  },
  {
    id: 'timing',
    name: 'Timing & Scoring',
    description: 'Transponder and timing system tools',
    tools: [
      {
        id: 'transponders',
        name: 'Transponder Management',
        description: 'Assign and track transponder numbers',
        icon: Radio,
        path: 'transponders',
        status: 'available',
        color: 'text-red-600 bg-red-100',
      },
      {
        id: 'timing-sync',
        name: 'Timing Sync',
        description: 'Sync with MyLaps and Speedhive',
        icon: Timer,
        path: 'timing-sync',
        status: 'coming-soon',
        color: 'text-indigo-600 bg-indigo-100',
      },
      {
        id: 'lap-times',
        name: 'Lap Time Analysis',
        description: 'View and analyze lap time data',
        icon: Activity,
        path: 'lap-times',
        status: 'coming-soon',
        color: 'text-cyan-600 bg-cyan-100',
      },
    ]
  },
  {
    id: 'credentials',
    name: 'Credentials & Access',
    description: 'Manage event credentials and pit access',
    tools: [
      {
        id: 'pit-passes',
        name: 'Pit Pass Scanner',
        description: 'Scan and validate pit passes',
        icon: QrCode,
        path: 'pit-passes',
        status: 'coming-soon',
        color: 'text-slate-600 bg-slate-100',
      },
      {
        id: 'credentials',
        name: 'Credential Printer',
        description: 'Print credentials and wristbands',
        icon: Printer,
        path: 'credentials',
        status: 'coming-soon',
        color: 'text-orange-600 bg-orange-100',
      },
    ]
  },
  {
    id: 'connectivity',
    name: 'Connectivity',
    description: 'Network and device management',
    tools: [
      {
        id: 'network-status',
        name: 'Network Status',
        description: 'Monitor network connectivity',
        icon: Wifi,
        path: 'network',
        status: 'coming-soon',
        color: 'text-green-600 bg-green-100',
      },
      {
        id: 'devices',
        name: 'Connected Devices',
        description: 'Manage connected timing devices',
        icon: Signal,
        path: 'devices',
        status: 'coming-soon',
        color: 'text-violet-600 bg-violet-100',
      },
    ]
  },
];

// Quick launch tools
const quickLaunchTools = [
  { id: 'lineup-monitor', name: 'Lineup Monitor', icon: Monitor, path: 'lineup-monitor' },
  { id: 'announcer', name: 'Announcer Screen', icon: Mic, path: 'announcer' },
  { id: 'transponders', name: 'Transponders', icon: Radio, path: 'transponders' },
];

// Recent activity (mock)
const recentActivity = [
  { tool: 'Lineup Monitor', action: 'Opened', time: '5 min ago', user: 'Chris O.' },
  { tool: 'Transponders', action: 'Updated #49', time: '15 min ago', user: 'John S.' },
  { tool: 'Announcer Screen', action: 'Opened', time: '1 hour ago', user: 'Sarah J.' },
];

// Tool Card Component
function ToolCard({ tool, seriesSlug }) {
  const Icon = tool.icon;
  const isAvailable = tool.status === 'available';

  return (
    <Link
      to={isAvailable ? `/${seriesSlug}/tools/${tool.path}` : '#'}
      className={`block ${!isAvailable ? 'cursor-not-allowed' : ''}`}
      onClick={(e) => !isAvailable && e.preventDefault()}
    >
      <div className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
        isAvailable
          ? 'bg-white border-slate-200 hover:border-rms-accent hover:shadow-md group'
          : 'bg-slate-50 border-slate-200 opacity-60'
      }`}>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${tool.color}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold ${isAvailable ? 'text-slate-800 group-hover:text-rms-accent' : 'text-slate-500'}`}>
              {tool.name}
            </h3>
            {!isAvailable && (
              <Badge variant="neutral" size="sm">Coming Soon</Badge>
            )}
          </div>
          <p className="text-sm text-slate-500 truncate">{tool.description}</p>
        </div>
        {isAvailable && (
          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-rms-accent transition-colors" />
        )}
      </div>
    </Link>
  );
}

// Category Section Component
function CategorySection({ category, seriesSlug }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{category.name}</CardTitle>
        <CardDescription>{category.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {category.tools.map(tool => (
            <ToolCard key={tool.id} tool={tool} seriesSlug={seriesSlug} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Active Sessions Component
function ActiveSessions() {
  const [sessions] = useState([
    { id: 1, tool: 'Lineup Monitor', device: 'Pit Tower iPad', started: '2:30 PM', status: 'active' },
    { id: 2, tool: 'Announcer Screen', device: 'Tower Mac', started: '2:15 PM', status: 'active' },
  ]);

  if (sessions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />
            Active Sessions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-slate-500">
            <Monitor className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No active tool sessions</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-500" />
          Active Sessions
        </CardTitle>
        <CardDescription>{sessions.length} tools currently in use</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {sessions.map(session => (
            <div
              key={session.id}
              className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <div>
                  <div className="font-medium text-slate-700 text-sm">{session.tool}</div>
                  <div className="text-xs text-slate-500">{session.device}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Since {session.started}</span>
                <button className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                  <Square className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Tools() {
  useParams();
  const currentSeries = useSeries();
  const seriesSlug = currentSeries?.slug;

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
            <Wrench className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h1 className="page-title">Tools</h1>
            <p className="page-subtitle">Operational tools for race day management</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick Launch */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="w-4 h-4 text-emerald-500" />
                Quick Launch
              </CardTitle>
              <CardDescription>Frequently used tools</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                {quickLaunchTools.map(tool => {
                  const Icon = tool.icon;
                  return (
                    <Link
                      key={tool.id}
                      to={`/${seriesSlug}/tools/${tool.path}`}
                      className="flex flex-col items-center gap-2 p-4 rounded-lg bg-slate-50 hover:bg-rms-accent/10 border border-slate-200 hover:border-rms-accent transition-all group"
                    >
                      <div className="w-12 h-12 rounded-lg bg-white border border-slate-200 group-hover:border-rms-accent flex items-center justify-center transition-colors">
                        <Icon className="w-6 h-6 text-slate-500 group-hover:text-rms-accent transition-colors" />
                      </div>
                      <span className="text-sm font-medium text-slate-700 group-hover:text-rms-accent transition-colors">
                        {tool.name}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Tool Categories */}
          {toolCategories.map(category => (
            <CategorySection
              key={category.id}
              category={category}
              seriesSlug={seriesSlug}
            />
          ))}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Active Sessions */}
          <ActiveSessions />

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentActivity.map((activity, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200"
                  >
                    <div>
                      <div className="text-sm font-medium text-slate-700">
                        {activity.tool}
                      </div>
                      <div className="text-xs text-slate-500">
                        {activity.action} by {activity.user}
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">{activity.time}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* External Tools */}
          <Card>
            <CardHeader>
              <CardTitle>External Tools</CardTitle>
              <CardDescription>Third-party integrations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <a
                href="https://speedhive.mylaps.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-orange-100 flex items-center justify-center">
                    <Timer className="w-4 h-4 text-orange-600" />
                  </div>
                  <span className="font-medium text-slate-700">Speedhive</span>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400" />
              </a>

              <a
                href="https://mylaps.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
                    <Radio className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="font-medium text-slate-700">MyLaps</span>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400" />
              </a>

              <a
                href="#"
                className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-purple-100 flex items-center justify-center">
                    <Monitor className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="font-medium text-slate-700">Live Timing Relay</span>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400" />
              </a>
            </CardContent>
          </Card>

          {/* Help */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Settings className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-800 mb-1">Need Help?</h4>
                  <p className="text-sm text-slate-500 mb-3">
                    Contact support for assistance with race day tools.
                  </p>
                  <Button variant="secondary" size="sm">
                    View Documentation
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
